import { Editor, VueNodeViewRenderer, VueRenderer } from "@tiptap/vue-3";
import tippy, { type GetReferenceClientRect } from "tippy.js";
import type { Component, ComputedRef, Ref } from "vue";
import {
    type MentionOptions as BaseMentionOptions,
} from "@tiptap/extension-mention";
import SuggestionList from "~/components/hd/SuggestionList.vue";
import { type DOMOutputSpec, Node as ProseMirrorNode } from "@tiptap/pm/model";
import SuggestionItem from "~/components/hd/SuggestionItem.vue";
import { escapeForRegEx, nodePasteRule } from "@tiptap/core";
import type {
    SuggestionMatch,
    Trigger,
} from "@tiptap/suggestion/src/findSuggestionMatch";
import type { ExtendedRegExpMatchArray } from "@tiptap/core/dist/packages/core/src/types";

export interface MentionOptions<T> {
    attributes?: (keyof T)[];
    matchKey: keyof T;
    items: T[] | Ref<T[]> | ComputedRef<T[]> | MaybeRefOrGetter<T[]>;
    name?: string;
    char?: string;
    prefix: string;
    suffix: string;
    configure?: Partial<BaseMentionOptions>;
    class: string;
    listComponent?: Component | string;
    suggestionComponent?: Component | string;
    component: Component | string;
    query: (props: { items: T[]; query: string; editor: Editor }) => T[];
    render: (
        item: T,
        context: Parameters<BaseMentionOptions["renderHTML"]>[0],
    ) => string;
}

export interface MentionItemContext<T> {
    index: number;
    item: T;
    select: () => void;
    active: boolean;
}

export default function useMention<T extends Record<string, any>>(
    baseOptions: Partial<MentionOptions<T>> = {},
) {
    const options: MentionOptions<T> = {
        attributes: ["label", "value"] as (keyof T)[],
        items: [],
        char: "@",
        prefix: "@",
        suffix: "",
        class: "",
        name: undefined,
        listComponent: undefined,
        suggestionComponent: undefined,
        allowSpaces: false,
        allowedPrefixes: null,
        matchKey: "value" as keyof T,
        render: () => "",
        ...baseOptions,
    } as MentionOptions<T>;

    if (options.prefix) options.char = options.prefix;

    const getOptions = (): T[] => {
        return (
            (options.items as Ref<T[]>).value
                ? (options.items as Ref<T[]>).value
                : options.items
        ) as T[];
    };

    const getMatch = (value: any): T | undefined => {
        return getOptions().find(
            (option) => option[options.matchKey as keyof T] == value,
        );
    };

    const renderText = (context: {
        options: BaseMentionOptions;
        node: ProseMirrorNode;
    }) => {
        return options?.render
            ? options.render(
                  getMatch(context.node.attrs[options.matchKey as string]) as T,
                  context,
              )
            : "";
    };

    const renderHTML = (context: {
        options: BaseMentionOptions;
        node: ProseMirrorNode;
    }): DOMOutputSpec => {
        const attributes = { ...context.options.HTMLAttributes };

        for (const key of Object.keys(context.node.attrs ?? {})) {
            attributes[`data-${key}`] = context.node.attrs[key];
        }

        return ["span", attributes, renderText(context)];
    };

    const state = ref();

    const Plugin = useUniqueMention(options.name ?? "mention", {});

    const mention = Plugin.extend({
        parseHTML() {
            return [
                {
                    tag: "span",
                    getAttrs: (element) => {
                        for (const attribute of (options?.attributes ??
                            []) as (keyof T)[]) {
                            if (
                                !(element as HTMLElement).hasAttribute(
                                    `data-${attribute}`,
                                )
                            )
                                return false;
                        }

                        if (
                            !element.classList.contains(
                                `hd-${options.name ?? "mention"}`,
                            )
                        ) {
                            return false;
                        }

                        return true;
                    },
                },
            ];
        },
        addPasteRules() {
            return [
                nodePasteRule({
                    find: new RegExp(
                        `/${options.prefix ?? ""}\\s*(.*?)\\s*${options.suffix ?? ""}/`,
                        "g",
                    ),
                    type: this.type,
                    getAttributes(match: ExtendedRegExpMatchArray) {
                        const item = getMatch(match[1]);

                        if (item) {
                            const attributes: Record<keyof T, any> =
                                {} as Record<keyof T, any>;

                            for (const attribute of (options?.attributes ??
                                []) as (keyof T)[])
                                attributes[attribute] = item[attribute];

                            return attributes;
                        }

                        return false;
                    },
                }),
            ];
        },
        addAttributes() {
            const setup: Record<string, Record<string, any>> = {};

            for (const attribute of options.attributes as string[]) {
                setup[attribute] = {
                    default: null,
                    parseHTML: (element: HTMLElement) => {
                        return element.getAttribute(`data-${attribute}`);
                    },
                    renderHTML: (attributes: Record<string, any>) => {
                        return {
                            [`data-${attribute}`]: attributes[attribute],
                        };
                    },
                };
            }
            return setup;
        },
    }).configure({
        ...options.configure,
        HTMLAttributes: {
            class: `hd-${options.name ?? "mention"} ${options.class}`.trim(),
        },
        renderHTML: renderHTML,
        renderText: renderText,
        getMatch(node: ProseMirrorNode) {
            return getMatch(node.attrs[options.matchKey as string]);
        },
        suggestion: {
            char: options.char,
            items: (props) => {
                return options.query({
                    query: props.query,
                    editor: props.editor as Editor,
                    items: getOptions(),
                });
            },
            command({ editor, range, props }) {
                // increase range.to by one when the next node is of type "text"
                // and starts with a space character
                const nodeAfter = editor.view.state.selection.$to.nodeAfter;
                const overrideSpace = nodeAfter?.text?.startsWith(" ");

                if (state.value && state.value.range) {
                    range = state.value.range;

                    let text = state.value.actualText;

                    if (options.suffix) {
                        range.to -= options.suffix.length;

                        for (const char of Array.from(
                            options.suffix,
                        ).reverse()) {
                            if (text.endsWith(char)) {
                                text = text.slice(0, -1);
                                range.to += 1;
                            }
                        }
                    }
                }

                if (overrideSpace) {
                    range.to += 1;
                }

                editor
                    .chain()
                    .focus()
                    .insertContentAt(range, [
                        {
                            type: options.name ?? "mention",
                            attrs: props,
                        },
                        // {
                        //     type: "text",
                        //     text: " ",
                        // },
                    ])
                    .run();

                window.getSelection()?.collapseToEnd();
            },
            findSuggestionMatch(config: Trigger): SuggestionMatch {
                const {
                    char,
                    allowSpaces,
                    allowedPrefixes,
                    startOfLine,
                    $position,
                } = config;

                const escapedPrefix = escapeForRegEx(char);
                const escapedSuffix = escapeForRegEx(
                    options.suffix ? options.suffix : char,
                );
                const suffix = new RegExp(`\\s${escapedSuffix}$`);
                const prefix = startOfLine ? "^" : "";

                const regexp = allowSpaces
                    ? new RegExp(
                          `${prefix}${escapedPrefix}.*?(?=\\s${escapedSuffix}|$)`,
                          "gm",
                      )
                    : new RegExp(
                          `${prefix}(?:^)?${escapedPrefix}[^\\s${escapedSuffix}]*`,
                          "gm",
                      );

                const text =
                    $position.nodeBefore?.isText && $position.nodeBefore.text;

                if (!text) {
                    return null;
                }

                const textFrom = $position.pos - text.length;

                const match = Array.from(text.matchAll(regexp)).pop() as {
                    index?: number;
                    input?: string;
                    0: string;
                };

                if (
                    !match ||
                    match.input === undefined ||
                    match.index === undefined
                ) {
                    return null;
                }

                // JavaScript doesn't have lookbehinds. This hacks a check that first character
                // is a space or the start of the line
                const matchPrefix = match.input.slice(
                    Math.max(0, match.index - 1),
                    match.index,
                );
                const matchPrefixIsAllowed = new RegExp(
                    `^[${allowedPrefixes?.join("")}\0]?$`,
                ).test(matchPrefix);

                if (allowedPrefixes !== null && !matchPrefixIsAllowed) {
                    return null;
                }

                // The absolute position of the match in the document
                const from = textFrom + match.index;
                let to = from + match[0].length;

                // Edge case handling; if spaces are allowed and we're directly in between
                // two triggers
                if (allowSpaces && suffix.test(text.slice(to - 1, to + 1))) {
                    match[0] += " ";
                    to += 1;
                }

                let matchText = match[0];

                if (options.suffix) {
                    matchText += options.suffix;
                    to += options.suffix.length;
                }

                // If the $position is located within the matched substring, return that range
                if (from < $position.pos && to >= $position.pos) {
                    return (state.value = {
                        range: {
                            from,
                            to,
                        },
                        query: match[0].slice(char.length),
                        text: matchText,
                        actualText: text,
                    });
                }

                return null;
            },
            render: () => {
                let component: VueRenderer;
                let popup: ReturnType<typeof tippy>;

                return {
                    onStart(props) {
                        component = new VueRenderer(
                            options.listComponent ?? SuggestionList,
                            {
                                props: {
                                    ...props,
                                    itemComponent: options.component,
                                    matchKey: options.matchKey,
                                    select: (item: T) => {
                                        const attributes: Record<keyof T, any> =
                                            {} as Record<keyof T, any>;

                                        for (const attribute of (options?.attributes ??
                                            []) as (keyof T)[])
                                            attributes[attribute] =
                                                item[attribute];

                                        props.command(attributes);
                                    },
                                },
                                editor: props.editor,
                            },
                        );

                        if (!props.clientRect) {
                            return;
                        }

                        popup = tippy("body", {
                            getReferenceClientRect:
                                props.clientRect as unknown as GetReferenceClientRect,
                            appendTo: () => document.body,
                            content: component?.element,
                            showOnCreate: true,
                            interactive: true,
                            trigger: "manual",
                            placement: "bottom-start",
                            theme: "transparent",
                        });
                    },

                    onUpdate(props) {
                        component?.updateProps(props);

                        if (!props.clientRect) {
                            return;
                        }

                        if (popup[0])
                            popup[0].setProps({
                                getReferenceClientRect:
                                    props.clientRect as unknown as GetReferenceClientRect,
                            });
                    },

                    onKeyDown(props) {
                        if (props.event.key === "Escape") {
                            if (popup) popup[0].hide();

                            return true;
                        }

                        return component?.ref?.onKeyDown(props);
                    },

                    onExit() {
                        if (popup) popup[0].destroy();
                        if (component) component.destroy();
                    },
                };
            },
        },
    });

    if (mention && mention.parent) {
        mention.parent.config.addNodeView = () =>
            VueNodeViewRenderer(options.suggestionComponent ?? SuggestionItem);
    }

    return mention;
}
